import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory, useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import { StatusCode } from "api/protocols";

import { capitalize, convertTime, displayCurrency, getCreditCardIcon } from "helpers/Helpers";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { dequeue, enqueue, IUIActions, showError, showSuccess } from "redux/actions/ui";
import Panel from "components/panel/Panel";
import { ButtonNew as Button } from "components/buttonNew";
import { IFacility } from "redux/reducers/models/facility";
import ReactDOM from "react-dom";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { GetClient } from "api/rpc/2024-04/customer/client";
import { GetClientGuest } from "api/rpc/2024-04/guest/client";
import { GetFacility } from "api/rpc/2024-04/customer/facility";
import { GetFacilityGuest } from "api/rpc/2024-04/guest/facility";
import { sortFacilities } from "redux/reducers/clientPortal";
import { UserActive } from "api/rpc";
import classNames from "classnames";
import Input from "components/form/input";
import "./foodOrderingCart.scss";
import { ICartState } from "redux/reducers/customer/cart";
import { ICartActions, putCart } from "redux/actions/customer/cart";
import { getFoodOrderingCartToken, getFoodOrderingCartTokenName } from "./Menu";
import { ICart, ICartLineItem, ICartTaxLine } from "redux/reducers/customer/models/cart";
import Tip from "components/tip/Tip";
import { PostCheckout } from "api/rpc/2024-04/guest/cart";
import { GetCustomerPaymentMethods, PostPaymentMethod, PostSetup } from "api/rpc/2024-04/customer/paymentMethod";
import { Select } from "components/select/index";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { PutCompleteOrder } from "api/rpc/2024-04/guest/order";
import { PostTransaction, PutCapture } from "api/rpc/2024-04/guest/transaction";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import CardSection from "components/cardSection/CardSection";
import Checkbox from "components/form/checkbox/Checkbox";
import validator from "validator";
import {
  faPlus,
  faXmark,
  faLocationDot,
  faClock,
  faGolfFlagHole,
  faSpinnerThird,
} from "@fortawesome/pro-light-svg-icons";
import {
  GetFoodOrderingPickupLocations,
  GetFoodOrderingSettings,
  PostFoodOrderingOrder,
} from "api/rpc/2024-04/guest/foodOrdering";
import { LocaleCurrency } from "helpers/Locale";
import { ClientPortalNavigation } from "../ClientPortalNavigation";
import { useTranslation } from "react-i18next";
import { UserType } from "redux/reducers/models/user";
import { PostCustomer } from "api/rpc/2024-04/guest/customer";

interface IParams {
  clientShortName: string;
  facilityShortName: string;
}

interface IFoodOrderingCartState {
  notes: string;
  addTipModal: boolean;
  tipAmount: number;
  orderOnline?: boolean;
  payAtCounter?: boolean;
  allow_off_course_orders: boolean;
  paymentMethods?: any[];
  selectedCard?: any;
  newCard?: boolean;
  allow_delivery: boolean;
}

interface IFoodOrderingCartProps {
  cartStore: ICartState;
  cartActions: ICartActions;
  uiActions: IUIActions;
}

interface IPaymentInformation {
  facility: Partial<IFacility>;
  cardElementComplete: boolean;
}

interface ICheckoutInformationField {
  data: string;
  isDirty: boolean;
  isValid: boolean;
  //   ref: React.MutableRefObject<HTMLElement>;
}

interface IFilterState {
  openAddPaymentMethod: boolean;
  openAddNewPaymentMethod: boolean;
  elementComplete: {
    cardNumber: boolean;
    cardExpiry: boolean;
    cardCvc: boolean;
  };
  selectedCardId: number;
  selectedCard: Record<string, any>;
  saveCard: boolean;
  firstNameField: ICheckoutInformationField;
  lastNameField: ICheckoutInformationField;
  emailAddressField: ICheckoutInformationField;
  phoneNumberField: ICheckoutInformationField;
  tee: string;
  pickup_location_id: number;
  pickup_locations: any[];
  pickup_times: string[];
  pickup_time: string;
  pickup_type: string;
}

interface IModifierGroupsState {
  [key: string]: ICartLineItem[];
}

export default function FoodOrderingCart(props: IFoodOrderingCartProps) {
  const { cartStore, cartActions, uiActions } = props;
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();

  const { clientShortName } = useParams<IParams>();
  const [paymentInProgress, setPaymentInProgress] = useState<boolean>(false);
  const [modifierGroups, setModifierGroups] = useState<IModifierGroupsState>({});

  const dispatch = useAppDispatch();
  const { clientPortalStore } = useAppSelector(store => store);

  const history = useHistory();
  const windowSize = useWindowSize();
  const { Option } = Select;

  const [paymentInformation, setPaymentInformation] = useState<IPaymentInformation>({
    facility: undefined,
    cardElementComplete: false,
  });

  const [cartState, setCartState] = useState<IFoodOrderingCartState>({
    notes: "",
    addTipModal: false,
    tipAmount: 0,
    orderOnline: false,
    payAtCounter: false,
    allow_off_course_orders: false,
    paymentMethods: [],
    selectedCard: null,
    newCard: false,
    allow_delivery: false,
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    openAddPaymentMethod: false,
    openAddNewPaymentMethod: false,
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    selectedCardId: undefined,
    selectedCard: undefined,
    saveCard: false,
    firstNameField: { data: "", isDirty: false, isValid: false },
    lastNameField: { data: "", isDirty: false, isValid: false },
    emailAddressField: { data: "", isDirty: false, isValid: false },
    phoneNumberField: { data: "", isDirty: false, isValid: false },
    tee: "",
    pickup_location_id: null,
    pickup_locations: [],
    pickup_times: [],
    pickup_time: "",
    pickup_type: "",
  });

  // Check if card inputs are filled
  const elementsComplete =
    filterState.elementComplete.cardNumber === true &&
    filterState.elementComplete.cardExpiry === true &&
    filterState.elementComplete.cardCvc === true;

  const holeLocations = [
    {
      id: 1,
      hole: "1st Tee",
    },
    {
      id: 2,
      hole: "2nd Tee",
    },
    {
      id: 3,
      hole: "3rd Tee",
    },
    {
      id: 4,
      hole: "4th Tee",
    },
    {
      id: 5,
      hole: "5th Tee",
    },
    {
      id: 6,
      hole: "6th Tee",
    },
    {
      id: 7,
      hole: "7th Tee",
    },
    {
      id: 8,
      hole: "8th Tee",
    },
    {
      id: 9,
      hole: "9th Tee",
    },
    {
      id: 10,
      hole: "10th Tee",
    },
    {
      id: 11,
      hole: "11th Tee",
    },
    {
      id: 12,
      hole: "12th Tee",
    },
    {
      id: 13,
      hole: "13th Tee",
    },
    {
      id: 14,
      hole: "14th Tee",
    },
    {
      id: 15,
      hole: "15th Tee",
    },
    {
      id: 16,
      hole: "16th Tee",
    },
    {
      id: 17,
      hole: "17th Tee",
    },
    {
      id: 18,
      hole: "18th Tee",
    },
  ];

  useEffect(() => {
    if (!clientPortalStore?.client) {
      void getClient();
    }
  }, []);

  useEffect(() => {
    const currentActiveUser = !!clientPortalStore?.user;

    if (currentActiveUser) {
      const source = axios.CancelToken.source();
      void loadPaymentMethods(source.token);

      return () => {
        source.cancel("Cancelled");
      };
    }
  }, [clientPortalStore.user]);

  useEffect(() => {
    void loadPickupLocations();
    void loadFoodOrderingSettings();
    void loadCart();
  }, [clientPortalStore?.selectedFacility, clientPortalStore?.client]);

  useEffect(() => {
    void groupModifiers();
  }, [cartStore?.cart?.line_items]);

  function groupModifiers() {
    const groups = cartStore?.cart?.line_items?.reduce(
      (prevModifierGroups: { [key: string]: Array<ICartLineItem> }, lineItem: any) => {
        if (lineItem.parent_id !== null) {
          return {
            ...prevModifierGroups,
            [lineItem.parent_id]: [...(prevModifierGroups[lineItem.parent_id] || []), lineItem],
          };
        } else {
          return prevModifierGroups;
        }
      },
      {},
    );

    setModifierGroups(groups);
  }

  async function loadPickupLocations() {
    if (clientPortalStore?.selectedFacility) {
      const params = {
        facility_id: clientPortalStore?.selectedFacility?.id,
      };
      const pickupLocationsRes = await GetFoodOrderingPickupLocations(params, true);

      if (pickupLocationsRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading pickup locations")));
        return;
      }

      setFilterState(prevState => ({ ...prevState, pickup_locations: pickupLocationsRes.data }));
    }
  }

  async function loadFoodOrderingSettings() {
    if (clientPortalStore?.selectedFacility) {
      const params = {
        facility_id: clientPortalStore?.selectedFacility?.id,
      };

      const foodOrderingSettingsRes = await GetFoodOrderingSettings(params, true);

      if (foodOrderingSettingsRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error getting food ordering settings")));
        return;
      }

      const today = new Date();

      const operatingHours = foodOrderingSettingsRes.data?.operating_hours?.find(
        (op: any) => op.day_of_week === moment(today).format("dddd"),
      );

      const openDateString = moment(today).format("LL") + " " + String(operatingHours?.open_time);
      const closeDateString = moment(today).format("LL") + " " + String(operatingHours?.close_time);

      const open = new Date(openDateString);
      const close = new Date(closeDateString);

      let diff = (open.getTime() - close.getTime()) / 1000;
      diff /= 30 * 60;

      const halfHourDiff = Math.abs(Math.round(diff));

      const timeArray: string[] = [];

      for (let i = 0; i <= halfHourDiff; i++) {
        if (moment(today).format("HH:mm") < moment(new Date(open.valueOf() + i * 1000 * 30 * 60)).format("HH:mm")) {
          timeArray.push(moment(new Date(open.valueOf() + i * 1000 * 30 * 60)).format("HH:mm"));
        }
      }

      setCartState(prevState => ({
        ...prevState,
        orderOnline: foodOrderingSettingsRes.data?.pay_online,
        payAtCounter: foodOrderingSettingsRes.data?.pay_at_counter,
        allow_off_course_orders: foodOrderingSettingsRes.data?.allow_off_course_orders,
        allow_delivery: foodOrderingSettingsRes.data?.allow_on_course_delivery,
      }));

      setFilterState(prevState => ({
        ...prevState,
        pickup_times: timeArray,
        pickup_type: window.sessionStorage.getItem("pickupType"),
      }));
    }
  }

  useEffect(() => {
    const type = window.sessionStorage.getItem("pickupType");

    if (type !== null) {
      setFilterState(prevState => ({ ...prevState, pickup_type: type }));
    }
  }, []);

  useEffect(() => {
    if (!cartState?.allow_off_course_orders) {
      setFilterState(prevState => ({ ...prevState, pickup_type: "pickup" }));
      window.sessionStorage.setItem("pickupType", "pickup");
    }
  }, [cartState?.allow_off_course_orders]);

  function navigateToConfirmation(orderToken: string) {
    history.push(`/portal/${clientShortName}/food-ordering/confirmation/${orderToken}`);
  }

  async function getClient() {
    let currentActiveUser = !!clientPortalStore?.user;
    let selectedClient = clientPortalStore?.client;
    let selectedFacility = clientPortalStore?.selectedFacility;

    dispatch(enqueue());

    const activeUserRes = await UserActive(false);
    if (activeUserRes?.status === StatusCode.OK) {
      currentActiveUser = true;
    }
    if (
      activeUserRes?.data?.user_type === UserType.CLIENT_ADMIN ||
      activeUserRes?.data?.user_type === UserType.FACILITY_ADMIN
    ) {
      setCartState(prevState => ({ ...prevState, adminUserLoggedIn: true }));
      dispatch(dequeue());

      return;
    }

    if (
      !clientPortalStore?.client ||
      !clientPortalStore?.selectedFacility ||
      (clientPortalStore?.selectedFacility?.favourite === undefined && currentActiveUser)
    ) {
      let clientRes;
      let facilityRes;
      if (currentActiveUser) {
        currentActiveUser = true;
        clientRes = await GetClient({ short_name: clientShortName }, false);
      } else {
        clientRes = await GetClientGuest({ short_name: clientShortName }, false);
      }
      if (clientRes.status !== StatusCode.OK || clientRes?.data?.length === 0) {
        dispatch(dequeue());
        dispatch(showError(t("Error loading client")));
        return;
      }
      selectedClient = clientRes?.data[0];
      if (currentActiveUser) {
        facilityRes = await GetFacility({ client_id: selectedClient?.id, order_by: "long_name" }, false);
      } else {
        facilityRes = await GetFacilityGuest({ client_id: selectedClient?.id, order_by: "long_name" }, false);
      }

      if (facilityRes?.status !== StatusCode.OK || facilityRes?.data?.length === 0) {
        dispatch(dequeue());
        dispatch(showError(t("Error loading facilities")));
        return;
      }

      //Sort by Favourite facilities
      const sortedFacilities = sortFacilities(facilityRes?.data);

      // selectedFacility = selectedFacility
      //   ? sortedFacilities?.find(facility => facility?.id === selectedFacility?.id)
      //   : clientPortalStore?.selectedFacility?.short_name
      //   ? sortedFacilities?.find(facility => facility?.short_name === clientPortalStore?.selectedFacility?.short_name)
      //   : sortedFacilities[0];

      selectedFacility = sortedFacilities?.find(
        facility => facility?.short_name === window.sessionStorage.getItem("facilityShortName"),
      );

      if (!selectedFacility && sortedFacilities?.length > 0) {
        selectedFacility = sortedFacilities[0];
      }

      selectedClient = { ...selectedClient, facilities: sortedFacilities };

      dispatch(
        clientPortalUpdate({
          client: selectedClient,
          selectedFacility,
          // waitlist_reference_id: waitlist_reference_id ?? null,
        }),
      );

      setPaymentInformation(prevState => ({
        ...prevState,
        facility: selectedFacility,
      }));
    }

    ReactDOM.unstable_batchedUpdates(() => {
      dispatch(dequeue());
      dispatch(
        clientPortalUpdate({
          user: currentActiveUser ? activeUserRes?.data : undefined,
        }),
      );
    });
    //   await checkLoginRequired(selectedFacility, currentActiveUser);
  }

  async function loadCart() {
    let cart: ICart = undefined;

    if (getFoodOrderingCartToken(clientPortalStore?.selectedFacility?.short_name)) {
      try {
        const getCartPromise: Promise<ICart> = cartStore?.isLoaded
          ? Promise.resolve(cartStore?.cart)
          : (cartActions.loadCart(
              {
                facility_id: clientPortalStore?.selectedFacility?.id,
                tokenName: getFoodOrderingCartTokenName(clientPortalStore?.selectedFacility?.short_name),
                token: getFoodOrderingCartToken(clientPortalStore?.selectedFacility?.short_name),
              },
              true,
            ) as unknown as Promise<ICart>);

        cart = await getCartPromise;
      } catch {
        uiActions.showError(t("Error getting cart"));
        uiActions.dequeue();
        return;
      }
    }

    if (cart?.status === "complete") {
      cartActions.cartClear({
        tokenName: getFoodOrderingCartTokenName(clientPortalStore?.selectedFacility?.short_name),
      });
      return;
    }

    if (cartStore.cart === null && cartStore.isLoaded === false && cart !== undefined) {
      dispatch({
        type: "cart.update",
        payload: {
          isLoaded: true,
          cart: cart,
        },
      });
    }
  }

  async function loadPaymentMethods(cancelToken?: CancelToken) {
    const paymentMethodRes = await GetCustomerPaymentMethods({ type: ["card"] }, true, cancelToken);
    if (paymentMethodRes?.status !== StatusCode.OK) {
      if (paymentMethodRes.message === "Cancelled") {
        return;
      }
      dispatch(showError(t("Error getting payment methods")));
      setCartState(prevState => ({ ...prevState, paymentMethods: [] }));
      return;
    }
    setCartState(prevState => ({ ...prevState, paymentMethods: paymentMethodRes?.data?.card }));
  }

  function sendToPage(address: string, openNewTab = false) {
    if (window.location.search.includes(address)) {
      return;
    } else if (openNewTab) {
      window.open(address);
      return;
    }
    history.push(address);
  }

  function handleAddTip(tipAmount: number) {
    cartActions.putCart({ cart_token: cartStore?.cart?.token, total_tip: tipAmount }, true);
  }

  async function reviewYourOrder() {
    setPaymentInProgress(true);

    function reviewYourOrderError(errorMessage: string) {
      uiActions.dequeue();
      uiActions.showError(errorMessage);
      cartActions.loadCart(
        {
          facility_id: clientPortalStore?.selectedFacility?.id,
          tokenName: getFoodOrderingCartTokenName(clientPortalStore?.selectedFacility?.short_name),
          token: getFoodOrderingCartToken(clientPortalStore?.selectedFacility?.short_name),
        },
        true,
      );
    }

    if (cartStore?.cart?.customer === null) {
      await PostCustomer(
        {
          email: filterState?.emailAddressField.data,
          first_name: filterState?.firstNameField.data,
          last_name: filterState?.lastNameField?.data,
        },
        true,
      );

      await dispatch(
        putCart(
          {
            cart_token: cartStore.cart.token,
            customer_email: clientPortalStore?.user
              ? clientPortalStore?.user?.email
              : filterState.emailAddressField.data,
          },
          false,
        ),
      );
    }

    uiActions.enqueue();

    try {
      const postCheckoutResponse = await PostCheckout({ cart_token: cartStore.cart.token }, false);
      const order = postCheckoutResponse.data?.order;

      if ((postCheckoutResponse.status as StatusCode) !== StatusCode.OK) {
        reviewYourOrderError(t("Error checking out order"));
        setPaymentInProgress(false);
        return;
      }

      if (order?.id === undefined || order?.token === undefined || order?.token === null) {
        // reviewYourOrderError(t("Order does not exist"));
        setPaymentInProgress(false);
        return;
      }

      const postFoodOrderParams = {
        order_id: order.id,
        tee: String(filterState?.tee),
        pickup_time: String(filterState?.pickup_time),
        pickup_location_id: filterState?.pickup_location_id,
        notes: cartStore?.cart?.notes ? cartStore?.cart?.notes : "",
        pickup_type: filterState?.pickup_type,
      };

      const postFoodOrderRes = await PostFoodOrderingOrder(postFoodOrderParams, false);

      if (postFoodOrderRes.status !== StatusCode.OK) {
        reviewYourOrderError(t("Error posting food order"));
        setPaymentInProgress(false);
        return;
      }

      const orderToken = order.token;
      const orderAmount = order.total_price;

      // If paying now process the payment
      if (filterState.selectedCard || elements.getElement("cardNumber") !== null) {
        const postTransactionResponse = await PostTransaction(
          {
            processing_type: "online",
            order_token: orderToken,
            kind: "authorization",
            source: "online",
            amount: orderAmount,
            payment_method: "card",
          },
          false,
        );

        const clientSecret: string = postTransactionResponse.data?.client_secret;

        if (postTransactionResponse.status !== StatusCode.OK || clientSecret === undefined || clientSecret === null) {
          reviewYourOrderError(t("Error posting transaction"));
          setPaymentInProgress(false);
          return;
        }

        if (!stripe || !elements) {
          reviewYourOrderError(t("Stripe error"));

          setPaymentInProgress(false);
          return;
        }

        let stripePaymentMethodRes;

        if (!clientPortalStore?.user) {
          stripePaymentMethodRes = await stripe.createPaymentMethod({
            type: "card",
            card: elements.getElement("cardNumber"),
          });
        }

        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: clientPortalStore?.user
            ? filterState?.selectedCard?.stripe_payment_method_id
            : stripePaymentMethodRes.paymentMethod.id,
        });

        if (stripeError) {
          reviewYourOrderError(stripeError.message);
          setPaymentInProgress(false);
          return;
        }

        const putCaptureResponse = await PutCapture(
          { payment_intent_id: paymentIntent.id, transaction_id: postTransactionResponse.data?.id },
          true,
        );

        if (putCaptureResponse.status !== StatusCode.OK) {
          reviewYourOrderError(t("Error capturing payment intent"));
          setPaymentInProgress(false);
          return;
        }

        const putCompleteOrder = await PutCompleteOrder({ token: orderToken }, false);

        if (putCompleteOrder.status !== StatusCode.OK) {
          reviewYourOrderError(t("Error completing order"));
          setPaymentInProgress(false);
          return;
        }
      }

      cartActions.cartClear({
        tokenName: getFoodOrderingCartTokenName(clientPortalStore?.selectedFacility?.short_name),
      });

      uiActions.dequeue();
      setPaymentInProgress(false);
      navigateToConfirmation(orderToken);
    } catch {
      uiActions.dequeue();
      setPaymentInProgress(false);
    }
  }

  async function handleAddNewCard() {
    if (!stripe || !elements) {
      dispatch(showError(t("Error occurred before card could save. Please reattempt")));
      return;
    }

    const tempCreditCards = [...cartState.paymentMethods];
    dispatch(enqueue());
    try {
      const stripePaymentMethodRes = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement("cardNumber"),
      });

      if (stripePaymentMethodRes.paymentMethod) {
        const setupRes = await PostSetup({}, false);

        if (setupRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error setting up card")));
        }

        if (stripePaymentMethodRes.paymentMethod.id !== "undefined" && setupRes.status === StatusCode.OK) {
          const stripeConfirmCardRes = await stripe.confirmCardSetup(
            setupRes.data.setup_intent.client_secret as string,
            {
              payment_method: stripePaymentMethodRes.paymentMethod.id,
            },
          );
          if (stripeConfirmCardRes.error) {
            dispatch(showError(stripeConfirmCardRes.error.message));
          } else {
            const postPaymentRes = await PostPaymentMethod(
              {
                payment_method_id: stripeConfirmCardRes.setupIntent.payment_method,
                save_card: filterState.saveCard,
                facility_id: clientPortalStore?.selectedFacility?.id,
              },
              false,
            );
            if (postPaymentRes.status !== StatusCode.OK) {
              dispatch(showError(t("Error adding card")));
            } else {
              tempCreditCards.push(postPaymentRes.data);

              setFilterState(prevState => ({
                ...prevState,
                selectedCardId: postPaymentRes.data.id,
                selectedCard: postPaymentRes?.data,
              }));

              setCartState(prevState => ({ ...prevState, paymentMethods: tempCreditCards }));
              void closeAddNewCard();
              dispatch(showSuccess(t("Successfully added card")));
            }
          }
        }
      } else {
        dispatch(showError(stripePaymentMethodRes.error.message));
      }
    } catch (e) {
      dispatch(showError(e as string));
      dispatch(dequeue());
    }
    dispatch(dequeue());
  }

  function closeAddNewCard() {
    elements.getElement(CardNumberElement).clear();
    elements.getElement(CardExpiryElement).clear();
    elements.getElement(CardCvcElement).clear();
    setFilterState(prevState => ({ ...prevState, openAddNewPaymentMethod: false, saveCard: false }));

    setFilterState(previousCheckoutInformation => {
      return {
        ...previousCheckoutInformation,
        firstNameField: {
          ...(previousCheckoutInformation["firstNameField" as keyof IFilterState] as Record<string, any>),
          data: "",
          isValid: false,
          isDirty: false,
        },
        lastNameField: {
          ...(previousCheckoutInformation["lastNameField" as keyof IFilterState] as Record<string, any>),
          data: "",
          isValid: false,
          isDirty: false,
        },
        emailAddressField: {
          ...(previousCheckoutInformation["emailAddressField" as keyof IFilterState] as Record<string, any>),
          data: "",
          isValid: false,
          isDirty: false,
        },
        phoneNumberField: {
          ...(previousCheckoutInformation["phoneNumberField" as keyof IFilterState] as Record<string, any>),
          data: "",
          isValid: false,
          isDirty: false,
        },
      };
    });
  }

  function handleCardSectionChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    setFilterState(prevState => ({
      ...prevState,
      elementComplete: { ...filterState.elementComplete, [e.elementType]: e.complete },
    }));
  }

  function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { checked } = e.target;
    setFilterState(prevState => ({ ...prevState, saveCard: checked }));
  }

  function handleInputFieldChange(id: string, value: string, isValid: boolean) {
    if (id === undefined || value === undefined || isValid === undefined) {
      return;
    }

    setFilterState(previousCheckoutInformation => {
      return {
        ...previousCheckoutInformation,
        [id]: {
          ...(previousCheckoutInformation[id as keyof IFilterState] as Record<string, any>),
          data: value,
          isValid: isValid,
          isDirty: true,
        },
      };
    });
  }

  function requiredValueExists(value: string) {
    return value !== "" && value !== undefined;
  }

  function emailIsValid(email: string) {
    if (email === undefined || email === null) {
      return false;
    } else {
      return validator.isEmail(email);
    }
  }

  function phoneNumberIsValid(phoneNumber: string) {
    return validator.isMobilePhone(phoneNumber, "en-CA");
  }

  function handleDropDownChange(value: any, property: string) {
    if (value === null) {
      return;
    }

    setFilterState(prev => ({ ...prev, [property]: value }));
  }

  function handlePickupTypeChange(pickupType: string) {
    window.sessionStorage.setItem("pickupType", pickupType);

    setFilterState(prevState => ({ ...prevState, pickup_type: pickupType }));
  }

  return (
    <div>
      {windowSize.width <= MOBILE_WIDTH ? (
        <div className="flex flex-col justify-between h-full">
          <div id="view" className="food-ordering-cart">
            <div className="food-ordering-close-cart">
              <FontAwesomeIcon
                size="1x"
                fixedWidth
                icon={faXmark}
                onClick={() => sendToPage(`/portal/${clientShortName}/food-ordering/menu`)}
                className="food-ordering-close-cart-icon"
              />
            </div>
            {/* <ClientPortalNavigation showNavOptions /> */}

            {!cartState.allow_off_course_orders ? (
              <div className="food-ordering-cart-pickup-dinein-toggle-container">
                <div className={classNames("food-ordering-cart-dinein-pickup-tab_selected", {})}>{t("Pickup")}</div>
              </div>
            ) : (
              <div className="food-ordering-cart-pickup-dinein-toggle-container">
                <div
                  onClick={() => handlePickupTypeChange("dine_in")}
                  className={classNames("food-ordering-cart-dinein-pickup-tab", {
                    "food-ordering-cart-dinein-pickup-tab_selected": filterState?.pickup_type === "dine_in",
                  })}
                >
                  {t("Dine in")}
                </div>
                <div
                  onClick={() => handlePickupTypeChange("pickup")}
                  className={classNames("food-ordering-cart-dinein-pickup-tab", {
                    "food-ordering-cart-dinein-pickup-tab_selected": filterState?.pickup_type === "pickup",
                  })}
                >
                  {t("Pickup")}
                </div>
                {cartState.allow_delivery ? (
                  <div
                    onClick={() => handlePickupTypeChange("delivery")}
                    className={classNames("food-ordering-cart-dinein-pickup-tab", {
                      "food-ordering-cart-dinein-pickup-tab_selected": filterState?.pickup_type === "delivery",
                    })}
                  >
                    {t("Delivery")}
                  </div>
                ) : null}
              </div>
            )}

            <div className="food-ordering-cart-items-container">
              {cartStore?.cart?.line_items?.map((product: any, index: number) => {
                if (product.parent_id === null) {
                  return (
                    <div key={index} className="food-ordering-cart-item-container">
                      <div className="food-ordering-cart-item-info-container">
                        <div className="food-ordering-cart-item-quantity">{product?.quantity}</div>

                        <div className="flex flex-col gap-0.5">
                          <p className="food-ordering-cart-item-title">{product?.product_title}</p>
                          <p className="food-ordering-cart-item-description">{product?.product?.description}</p>

                          {modifierGroups &&
                            product.id.toString() in modifierGroups &&
                            modifierGroups[product.id.toString()].map(modifier => {
                              // const modifierDiscounts = [...discountLines]?.filter(
                              //   discount => discount?.cart_line_item_id === modifier?.id,
                              // );
                              return (
                                <React.Fragment key={modifier.id}>
                                  <div className="flex items-center gap-2 ml-3">
                                    <p className="food-ordering-cart-item-description">+</p>
                                    <div className="flex gap-2">
                                      <p className="food-ordering-cart-item-description">{modifier?.quantity}</p>
                                      <div className="flex">
                                        <div className="food-ordering-cart-item-description">
                                          {modifier?.product_title}
                                          {modifier?.product_title !== modifier?.variant_title && (
                                            <p className="food-ordering-cart-item-description">
                                              {" "}
                                              {modifier?.variant_title}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <span className="food-ordering-cart-item-description">
                                      {/* {modifierDiscounts?.length > 0 && (
                                                            <span className="cart-line-items-new-discount cart-line-items-new-discount-total">
                                                              <LocaleCurrency currency="cad" amount={modifier?.price * modifier?.quantity} />
                                                            </span>
                                                          )}
                                                          <LocaleCurrency currency="cad" amount={modifier?.subtotal_price} /> */}

                                      <LocaleCurrency currency="cad" amount={modifier?.subtotal_price} />
                                    </span>
                                  </div>
                                </React.Fragment>
                              );
                            })}
                        </div>
                      </div>

                      <div className="flex gap-5">
                        <div className="food-ordering-cart-item-add-to-cart-button-container">
                          <p className="food-ordering-cart-item-price">
                            {displayCurrency("cad", product?.subtotal_price as number)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>

          <div className="food-ordering-cart-totals-container">
            <div className="mt-6">
              <div className="food-ordering-cart-subtotal-container">
                <p className="food-ordering-cart-subtotal-text">{t("Subtotal")}</p>
                <p className="food-ordering-cart-subtotal-price-text">
                  {" "}
                  {displayCurrency("cad", cartStore?.cart?.subtotal_price)}{" "}
                </p>
              </div>
              {cartStore?.cart?.tax_lines?.map((taxLine: ICartTaxLine, index: number) => {
                return (
                  <div className="food-ordering-cart-subtotal-container" key={index}>
                    <p className="food-ordering-cart-subtotal-text">{taxLine?.title}</p>
                    <p className="food-ordering-cart-subtotal-price-text"> {displayCurrency("cad", taxLine?.price)} </p>
                  </div>
                );
              })}
            </div>

            <div className="food-ordering-cart-total-container">
              <p className="food-ordering-cart-total-text">{t("Total")}</p>
              <p className="food-ordering-cart-total-price-text">
                {" "}
                {displayCurrency("cad", cartStore?.cart?.total_price)}{" "}
              </p>
            </div>

            <div>
              <Tip
                handleTipTeeSheet={tip => handleAddTip(tip)}
                totalTipAmount={cartStore?.cart?.total_tip}
                totalCartPrice={cartStore?.cart?.total_price}
                darkerButton
                smallerText
                //  order={order}
              />
            </div>

            <div className="food-ordering-options-container">
              <div className="food-ordering-option-container">
                <div className="flex gap-2 items-center w-full">
                  <div>
                    <FontAwesomeIcon
                      size="1x"
                      fixedWidth
                      icon={faLocationDot}
                      //   onClick={() => sendToPage(`/portal/${clientShortName}/food-ordering/menu`)}
                      className="option-icon"
                    />
                  </div>

                  <p className="option-title">
                    {filterState?.pickup_type === "dine_in" ? "Dining Location" : "Pickup Location"}{" "}
                  </p>
                </div>
                <div className="food-ordering-cart-dropdown-width">
                  <Select onChange={(value: any) => handleDropDownChange(value, "pickup_location_id")}>
                    {filterState.pickup_type !== "dine_in" ? (
                      <>
                        <React.Fragment>
                          <p style={{ padding: "4px 8px" }}>
                            <b>{t("On Course")}</b>
                          </p>
                        </React.Fragment>

                        {filterState?.pickup_locations
                          ?.filter(location => location.type === "on_course")
                          .map((location, index) => {
                            return (
                              <Option key={index} value={location.id} name={location.name}>
                                <div>
                                  <div>{location.name}</div>
                                </div>
                              </Option>
                            );
                          })}

                        <hr />

                        <React.Fragment>
                          <p style={{ padding: "4px 8px" }}>
                            <b>{t("Off Course")}</b>
                          </p>
                        </React.Fragment>

                        {filterState?.pickup_locations
                          ?.filter(location => location?.type === "off_course")
                          .map((location, index) => {
                            return (
                              <Option key={index} value={location?.id}>
                                <div>
                                  <div>{location?.name}</div>
                                </div>
                              </Option>
                            );
                          })}
                      </>
                    ) : null}

                    {filterState?.pickup_type === "dine_in" ? (
                      <>
                        {filterState?.pickup_locations
                          ?.filter(location => location?.type === "dining")
                          .map((location, index) => {
                            return (
                              <Option key={index} value={location?.id}>
                                <div>
                                  <div>{location?.name}</div>
                                </div>
                              </Option>
                            );
                          })}
                      </>
                    ) : (
                      <></>
                    )}
                  </Select>
                </div>
              </div>
              <div className="food-ordering-option-container">
                <div className="flex gap-2 items-center w-full">
                  <div>
                    <FontAwesomeIcon
                      size="1x"
                      fixedWidth
                      icon={faClock}
                      //   onClick={() => sendToPage(`/portal/${clientShortName}/food-ordering/menu`)}
                      className="option-icon"
                    />
                  </div>

                  <p className="option-title">
                    {filterState.pickup_type === "dine_in" ? "Dining Time" : t("Pickup Time")}
                  </p>
                </div>

                <div className="food-ordering-cart-dropdown-width">
                  <Select onChange={(value: any) => handleDropDownChange(value, "pickup_time")}>
                    {filterState?.pickup_times?.map((time, index) => {
                      return (
                        <Option key={index} value={time}>
                          <div>
                            <div>{convertTime(time)}</div>
                          </div>
                        </Option>
                      );
                    })}
                  </Select>

                  {/* <TimePick
          value={filterState?.pickup_time}
          onChange={(timeString) => setFilterState((prev) => ({ ...prev, pickup_time: timeString }))}
          size="medium"
          status={filterState.pickup_time === "" ? "warning" : undefined}
        /> */}
                </div>
              </div>
              <div className="food-ordering-option-container-last-option">
                <div className="flex gap-2 items-center w-full">
                  <div>
                    <FontAwesomeIcon
                      size="1x"
                      fixedWidth
                      icon={faGolfFlagHole}
                      //   onClick={() => sendToPage(`/portal/${clientShortName}/food-ordering/menu`)}
                      className="option-icon"
                    />
                  </div>

                  <p className="option-title">{t("Current Tee")}</p>
                </div>

                <div className="food-ordering-cart-dropdown-width">
                  <Select
                    // onChange={handleHoleChange}
                    onChange={(value: any) => handleDropDownChange(value, "tee")}
                  >
                    {holeLocations?.map((hole, index) => {
                      return (
                        <Option key={index} value={hole.id}>
                          <div>
                            <div>{hole.hole}</div>
                          </div>
                        </Option>
                      );
                    })}
                  </Select>
                </div>
              </div>

              {!!cartState?.orderOnline && !filterState?.selectedCard && (
                <Button
                  block
                  type="secondary"
                  size="medium"
                  className="mt-3 gap-2 items-center"
                  onClick={() => setFilterState(prevState => ({ ...prevState, openAddPaymentMethod: true }))}
                >
                  <FontAwesomeIcon size="1x" fixedWidth icon={faPlus} />
                  {t("Add payment method")}
                </Button>
              )}

              {filterState?.selectedCard && (
                <div className="food-ordering-payment-method-container mt-3 ">
                  <div>
                    <FontAwesomeIcon
                      className="payment-method-item-icon"
                      size="2x"
                      icon={getCreditCardIcon(filterState?.selectedCard.brand as string)}
                    />
                  </div>
                  <div>
                    <div className="food-ordering-payment-method-title">
                      {capitalize(filterState?.selectedCard.brand as string)} {t("ending in")}{" "}
                      {filterState?.selectedCard.last4}
                    </div>

                    {/* <div className="food-ordering-payment-method-expiry">Expiry {expiresOn}</div> */}
                  </div>
                  <div className="w-1/4 justify-end flex">
                    <FontAwesomeIcon
                      className="payment-method-item-icon"
                      size="1x"
                      icon={faXmark}
                      onClick={() =>
                        setFilterState(prevState => ({
                          ...prevState,
                          selectedCard: undefined,
                          openAddPaymentMethod: true,
                        }))
                      }
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="food-ordering-cart-checkout-button-container">
              {cartState.orderOnline ? (
                <Button
                  block
                  type="dark-gray"
                  size="medium"
                  onClick={reviewYourOrder}
                  loading={paymentInProgress}
                  disabled={
                    (filterState?.selectedCard === undefined &&
                      !paymentInformation?.cardElementComplete &&
                      cartState.orderOnline) ||
                    (filterState.pickup_type === "delivery" && filterState?.tee === "") ||
                    (filterState.pickup_type !== "delivery" && filterState?.pickup_time === "") ||
                    (filterState.pickup_type !== "delivery" && filterState?.pickup_location_id === null)
                  }
                >
                  {" "}
                  {t("Pay now")}
                </Button>
              ) : null}

              {cartState.payAtCounter ? (
                <Button
                  block
                  type="dark-gray"
                  size="medium"
                  onClick={reviewYourOrder}
                  loading={paymentInProgress}
                  className="ml-0"
                  disabled={
                    (cartState.payAtCounter && !cartStore.cart?.customer) ||
                    (filterState.pickup_type === "delivery" && filterState?.tee === "") ||
                    (filterState.pickup_type !== "delivery" && filterState?.pickup_time === "") ||
                    (filterState.pickup_type !== "delivery" && filterState?.pickup_location_id === null)
                  }
                >
                  {" "}
                  {!cartStore?.cart?.customer ? t("Login to pay at counter") : t("Pay at counter")}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <ClientPortalNavigation showNavOptions />

          <div className="food-ordering-cart-desktop-container">
            <div className="food-ordering-cart-desktop-container_dropdown_container">
              <div className="flex">
                <p className="food-ordering-cart-desktop-container_order_details_title">{t("Order Details")}</p>

                <div className="food-ordering-cart-desktop-container_pickup_dinein_container">
                  {!cartState.allow_off_course_orders ? (
                    <div className="food-ordering-cart-pickup-dinein-toggle-container">
                      <div className={classNames("food-ordering-cart-dinein-pickup-tab_selected", {})}>
                        {t("Pickup")}
                      </div>
                    </div>
                  ) : (
                    <div className="food-ordering-cart-pickup-dinein-toggle-container">
                      <div
                        onClick={() => handlePickupTypeChange("dine_in")}
                        className={classNames("food-ordering-cart-dinein-pickup-tab", {
                          "food-ordering-cart-dinein-pickup-tab_selected": filterState?.pickup_type === "dine_in",
                        })}
                      >
                        {t("Dine in")}
                      </div>
                      <div
                        onClick={() => handlePickupTypeChange("pickup")}
                        className={classNames("food-ordering-cart-dinein-pickup-tab", {
                          "food-ordering-cart-dinein-pickup-tab_selected": filterState?.pickup_type === "pickup",
                        })}
                      >
                        {t("Pickup")}
                      </div>
                      {cartState.allow_delivery ? (
                        <div
                          onClick={() => handlePickupTypeChange("delivery")}
                          className={classNames("food-ordering-cart-dinein-pickup-tab", {
                            "food-ordering-cart-dinein-pickup-tab_selected": filterState?.pickup_type === "delivery",
                          })}
                        >
                          {t("Delivery")}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
              <div className="food-ordering-options-container">
                {filterState.pickup_type === "pickup" || filterState.pickup_type === "dine_in" ? (
                  <div className="food-ordering-option-container">
                    <div className="flex gap-2 items-center w-full">
                      <div>
                        <FontAwesomeIcon
                          size="1x"
                          fixedWidth
                          icon={faLocationDot}
                          //   onClick={() => sendToPage(`/portal/${clientShortName}/food-ordering/menu`)}
                          className="option-icon"
                        />
                      </div>

                      <p className="option-title">
                        {filterState?.pickup_type === "dine_in" ? "Dining Location" : "Pickup Location"}{" "}
                      </p>
                    </div>

                    <div className="food-ordering-cart-dropdown-width">
                      <Select
                        onChange={(value: any) =>
                          value !== null ? handleDropDownChange(value, "pickup_location_id") : null
                        }

                        //   onChange={handleYearChange}
                        //   defaultValue={cartState.selectedPaymentMethod ?? null}
                      >
                        {filterState.pickup_type !== "dine_in" ? (
                          <>
                            <React.Fragment>
                              <p style={{ padding: "4px 8px" }}>
                                <b>{t("On Course")}</b>
                              </p>
                            </React.Fragment>

                            {filterState?.pickup_locations
                              ?.filter(location => location.type === "on_course")
                              .map((location, index) => {
                                return (
                                  <Option key={index} value={location.id}>
                                    <div>
                                      <div>{location.name}</div>
                                    </div>
                                  </Option>
                                );
                              })}

                            <hr />

                            <React.Fragment>
                              <p style={{ padding: "4px 8px" }}>
                                <b>{t("Off Course")}</b>
                              </p>
                            </React.Fragment>

                            {filterState?.pickup_locations
                              ?.filter(location => location?.type === "off_course")
                              .map((location, index) => {
                                return (
                                  <Option key={index} value={location?.id}>
                                    <div>
                                      <div>{location?.name}</div>
                                    </div>
                                  </Option>
                                );
                              })}
                          </>
                        ) : null}

                        {filterState?.pickup_type === "dine_in" ? (
                          <>
                            {filterState?.pickup_locations
                              ?.filter(location => location?.type === "dining")
                              .map((location, index) => {
                                return (
                                  <Option key={index} value={location?.id}>
                                    <div>
                                      <div>{location?.name}</div>
                                    </div>
                                  </Option>
                                );
                              })}
                          </>
                        ) : (
                          <></>
                        )}
                      </Select>
                    </div>
                  </div>
                ) : null}
                {filterState.pickup_type === "pickup" || filterState.pickup_type === "dine_in" ? (
                  <div className="food-ordering-option-container">
                    <div className="flex gap-2 items-center w-full">
                      <div>
                        <FontAwesomeIcon
                          size="1x"
                          fixedWidth
                          icon={faClock}
                          //   onClick={() => sendToPage(`/portal/${clientShortName}/food-ordering/menu`)}
                          className="option-icon"
                        />
                      </div>
                      <p className="option-title">
                        {filterState.pickup_type === "dine_in" ? "Dining Time" : t("Pickup Time")}
                      </p>
                    </div>

                    <div className="food-ordering-cart-dropdown-width">
                      <Select onChange={(value: any) => handleDropDownChange(value, "pickup_time")}>
                        {filterState?.pickup_times?.map((time, index) => {
                          return (
                            <Option key={index} value={time}>
                              <div>
                                <div>{convertTime(time)}</div>
                              </div>
                            </Option>
                          );
                        })}
                      </Select>
                    </div>
                  </div>
                ) : null}
                {filterState.pickup_type === "delivery" ? (
                  <div className="food-ordering-option-container-last-option">
                    <div className="flex gap-2 items-center w-full">
                      <div>
                        <FontAwesomeIcon
                          size="1x"
                          fixedWidth
                          icon={faGolfFlagHole}
                          //   onClick={() => sendToPage(`/portal/${clientShortName}/food-ordering/menu`)}
                          className="option-icon"
                        />
                      </div>
                      <p className="option-title">{t("Current Tee")}</p>
                    </div>

                    <div className="food-ordering-cart-dropdown-width">
                      <Select
                        // onChange={handleHoleChange}
                        onChange={(value: any) => handleDropDownChange(value, "tee")}
                      >
                        {holeLocations?.map((hole, index) => {
                          return (
                            <Option key={index} value={hole.id}>
                              <div>
                                <div>{hole.hole}</div>
                              </div>
                            </Option>
                          );
                        })}
                      </Select>
                    </div>
                  </div>
                ) : null}

                {!!cartState?.orderOnline && !filterState?.selectedCard && (
                  <Button
                    block
                    type="secondary"
                    size="medium"
                    className="mt-4 gap-2 items-center"
                    onClick={() => setFilterState(prevState => ({ ...prevState, openAddPaymentMethod: true }))}
                  >
                    <FontAwesomeIcon size="1x" fixedWidth icon={faPlus} />
                    {"Add payment method"}
                  </Button>
                )}

                {filterState?.selectedCard && (
                  <div className="food-ordering-desktop-payment-method-container mt-4 ">
                    <div className="flex items-center gap-4">
                      <FontAwesomeIcon
                        className="payment-method-item-icon"
                        size="2x"
                        icon={getCreditCardIcon(filterState?.selectedCard.brand as string)}
                      />
                      <div className="food-ordering-payment-method-title">
                        {capitalize(filterState?.selectedCard.brand as string)} {t("ending in")}{" "}
                        {filterState?.selectedCard.last4}
                      </div>
                    </div>
                    {/* <div>
                     

                      <div className="food-ordering-payment-method-expiry">Expiry {expiresOn}</div>
                    </div> */}
                    <div className="w-1/4 justify-end flex">
                      <FontAwesomeIcon
                        className="payment-method-item-icon"
                        size="1x"
                        icon={faXmark}
                        onClick={() =>
                          setFilterState(prevState => ({
                            ...prevState,
                            selectedCard: undefined,
                            openAddPaymentMethod: true,
                          }))
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="food-ordering-cart-desktop-container_summary_container">
                <p className="food-ordering-cart-desktop-container_summary_title">{t("Summary")}</p>
                <div className="food-ordering-cart-items-container">
                  {cartStore?.cart?.line_items?.map((product: any, index: number) => {
                    if (product.parent_id === null) {
                      return (
                        <div key={index} className="food-ordering-cart-item-container">
                          <div className="food-ordering-cart-item-info-container">
                            <div className="food-ordering-cart-item-quantity">{product?.quantity}</div>

                            <div className="flex flex-col gap-0.5">
                              <p className="food-ordering-cart-item-title">{product?.product_title}</p>
                              <p className="food-ordering-cart-item-description">{product?.product?.description}</p>

                              {modifierGroups &&
                                product.id.toString() in modifierGroups &&
                                modifierGroups[product.id.toString()].map(modifier => {
                                  // const modifierDiscounts = [...discountLines]?.filter(
                                  //   discount => discount?.cart_line_item_id === modifier?.id,
                                  // );
                                  return (
                                    <React.Fragment key={modifier.id}>
                                      <div className="flex items-center gap-2 ml-3">
                                        <p className="food-ordering-cart-item-description">+</p>
                                        <div className="flex gap-2">
                                          <p className="food-ordering-cart-item-description">{modifier?.quantity}</p>
                                          <div className="flex">
                                            <div className="food-ordering-cart-item-description">
                                              {modifier?.product_title}
                                              {modifier?.product_title !== modifier?.variant_title && (
                                                <p className="food-ordering-cart-item-description">
                                                  {" "}
                                                  {modifier?.variant_title}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <span className="food-ordering-cart-item-description">
                                          <LocaleCurrency currency="cad" amount={modifier?.subtotal_price} />
                                        </span>
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                            </div>
                          </div>

                          <div className="flex gap-5">
                            <div className="food-ordering-cart-item-add-to-cart-button-container">
                              <p className="food-ordering-cart-item-price">
                                {displayCurrency("cad", product?.subtotal_price as number)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
                <div>
                  <div className="mt-6">
                    <div className="food-ordering-cart-subtotal-container">
                      <p className="food-ordering-cart-subtotal-text">{t("Subtotal")}</p>
                      <p className="food-ordering-cart-subtotal-price-text">
                        {" "}
                        {displayCurrency("cad", cartStore?.cart?.subtotal_price)}{" "}
                      </p>
                    </div>
                    {cartStore?.cart?.tax_lines?.map((taxLine: ICartTaxLine, index: number) => {
                      return (
                        <div className="food-ordering-cart-subtotal-container" key={index}>
                          <p className="food-ordering-cart-subtotal-text">{taxLine?.title}</p>
                          <p className="food-ordering-cart-subtotal-price-text">
                            {" "}
                            {displayCurrency("cad", taxLine?.price)}{" "}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="food-ordering-cart-total-container">
                    {cartStore?.cart?.total_tip > 0 || cartStore?.cart?.status === "void" ? (
                      <span className="food-ordering-cart-total-text">{t("Tip")}</span>
                    ) : (
                      <span></span>
                    )}
                    {cartStore?.cart?.total_tip != null ? (
                      <span className="food-ordering-cart-total-text">
                        <Tip
                          handleTipTeeSheet={tip => handleAddTip(tip)}
                          totalTipAmount={cartStore?.cart?.total_tip}
                          totalCartPrice={cartStore?.cart?.total_price}
                          darkerButton
                          smallerText
                        />
                      </span>
                    ) : (
                      <FontAwesomeIcon className="cart-totals-new-spinner" size="1x" icon={faSpinnerThird} spin fade />
                    )}
                  </div>

                  <div className="food-ordering-cart-total-container">
                    <p className="food-ordering-cart-total-text">{t("Total")}</p>
                    <p className="food-ordering-cart-total-price-text">
                      {" "}
                      {displayCurrency("cad", cartStore?.cart?.total_price)}{" "}
                    </p>
                  </div>

                  <div className="food-ordering-cart-checkout-button-container">
                    {cartState.orderOnline ? (
                      <Button
                        block
                        type="dark-gray"
                        size="medium"
                        onClick={reviewYourOrder}
                        loading={paymentInProgress}
                        disabled={
                          (filterState?.selectedCard === undefined &&
                            !paymentInformation?.cardElementComplete &&
                            cartState.orderOnline) ||
                          (filterState.pickup_type === "delivery" && filterState?.tee === "") ||
                          (filterState.pickup_type !== "delivery" && filterState?.pickup_time === "") ||
                          (filterState.pickup_type !== "delivery" && filterState?.pickup_location_id === null)
                        }
                      >
                        {" "}
                        {t("Pay now")}
                      </Button>
                    ) : null}

                    {cartState.payAtCounter ? (
                      <Button
                        block
                        type="dark-gray"
                        size="medium"
                        onClick={reviewYourOrder}
                        loading={paymentInProgress}
                        className="ml-0"
                        disabled={
                          (cartState.payAtCounter && !cartStore.cart?.customer) ||
                          (filterState.pickup_type === "delivery" && filterState?.tee === "") ||
                          (filterState.pickup_type !== "delivery" && filterState?.pickup_time === "") ||
                          (filterState.pickup_type !== "delivery" && filterState?.pickup_location_id === null)
                        }
                      >
                        {" "}
                        {!cartStore?.cart?.customer ? t("Login to pay at counter") : t("Pay at counter")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Panel
        open={filterState.openAddPaymentMethod}
        onClose={() => setFilterState(prevState => ({ ...prevState, openAddPaymentMethod: false }))}
        animate
        pagePercent={95}
        darkerButton
        heading={
          <div className="flex justify-start gap-2" style={{ alignItems: "center" }}>
            <div className="text-semibold">{t("Select Card")}</div>
          </div>
        }
      >
        <div>
          {cartState?.paymentMethods?.map((method, index) => {
            const date = new Date(method?.exp_year as number, method?.exp_month - 1);
            const expiresOn = moment(date).format("MM/YYYY");

            return (
              <div
                key={index}
                className={classNames("food-ordering-payment-method-container", {
                  "food-ordering-payment-method-container_selected": method.id === filterState?.selectedCard?.id,
                })}
                onClick={() =>
                  setFilterState(prevState => ({
                    ...prevState,
                    selectedCard: method.id === filterState?.selectedCard?.id ? undefined : method,
                    openAddPaymentMethod: false,
                  }))
                }

                // onClick={handleSelectPaymentMethod}
              >
                <div>
                  <FontAwesomeIcon
                    className="payment-method-item-icon"
                    size="2x"
                    icon={getCreditCardIcon(method.brand as string)}
                  />
                </div>
                <div>
                  <div className="food-ordering-payment-method-title">
                    {capitalize(method.brand as string)} {t("ending in")} {method.last4}
                  </div>
                  <div className="food-ordering-payment-method-expiry">
                    {t("Expiry")} {expiresOn}
                  </div>{" "}
                </div>
              </div>
            );
          })}

          <div className="flex">
            <Button
              type="text"
              size="medium"
              className="mt-3 gap-2"
              onClick={() => setFilterState(prevState => ({ ...prevState, openAddNewPaymentMethod: true }))}
            >
              <FontAwesomeIcon size="1x" fixedWidth icon={faPlus} />
              {t("Add new payment method")}
            </Button>
          </div>
        </div>
      </Panel>

      <Panel
        open={filterState.openAddNewPaymentMethod}
        onClose={closeAddNewCard}
        animate
        pagePercent={95}
        primaryAction={{
          onClick: clientPortalStore?.user ? handleAddNewCard : reviewYourOrder,
          label: clientPortalStore?.user ? t("Add card") : t("Place order"),
          disabled: clientPortalStore?.user
            ? !elementsComplete
              ? true
              : false
            : filterState.firstNameField.data === "" ||
              (filterState.firstNameField.isDirty && !filterState.firstNameField.isValid) ||
              filterState.lastNameField.data === "" ||
              (filterState.lastNameField.isDirty && !filterState.lastNameField.isValid) ||
              filterState.emailAddressField.data === "" ||
              (filterState.emailAddressField.isDirty && !filterState.emailAddressField.isValid) ||
              filterState.phoneNumberField.data === "" ||
              (filterState.phoneNumberField.isDirty && !filterState.phoneNumberField.isValid) ||
              !elementsComplete
            ? true
            : false,
        }}
        loading={paymentInProgress}
        darkerButton
        heading={
          <div style={{ alignItems: "center" }}>
            <div className="text-semibold">{t("Payment Method")}</div>
            <div className="">{t("Add your payment details")}</div>
            <hr />
          </div>
        }
      >
        {!clientPortalStore?.user && (
          <div>
            <p>{t("Customer Info")}</p>
            <FormLayout>
              <FormLayout.Group>
                <span>
                  <Input
                    error={filterState.firstNameField.isDirty && !filterState.firstNameField.isValid}
                    value={filterState.firstNameField.data}
                    label={t("First Name")}
                    onChange={(event: any) =>
                      handleInputFieldChange(
                        "firstNameField",
                        event?.target?.value as string,
                        requiredValueExists(event?.target?.value as string),
                      )
                    }
                  />
                </span>
                <span>
                  <Input
                    error={filterState.lastNameField.isDirty && !filterState.lastNameField.isValid}
                    value={filterState.lastNameField.data}
                    label={t("Last Name")}
                    onChange={(event: any) =>
                      handleInputFieldChange(
                        "lastNameField",
                        event?.target?.value as string,
                        requiredValueExists(event?.target?.value as string),
                      )
                    }
                  />
                </span>
              </FormLayout.Group>
              <FormLayout.Group>
                <span>
                  <Input
                    error={filterState.emailAddressField.isDirty && !filterState.emailAddressField.isValid}
                    value={filterState.emailAddressField.data}
                    label={t("Email Address")}
                    onChange={(event: any) =>
                      handleInputFieldChange(
                        "emailAddressField",
                        event?.target?.value as string,
                        emailIsValid(event?.target?.value as string),
                      )
                    }
                  />
                </span>
                <span>
                  <Input
                    error={filterState.phoneNumberField.isDirty && !filterState.phoneNumberField.isValid}
                    value={filterState.phoneNumberField.data}
                    label={t("Phone Number")}
                    onChange={(event: any) =>
                      handleInputFieldChange(
                        "phoneNumberField",
                        event?.target?.value as string,
                        phoneNumberIsValid(event?.target?.value as string),
                      )
                    }
                  />
                </span>
              </FormLayout.Group>
            </FormLayout>
          </div>
        )}

        <div>
          <p>{t("Card Details")}</p>
          <div>
            <Form>
              <FormLayout>
                <FormLayout.Group>{<CardSection onChange={handleCardSectionChange} type="one-row" />}</FormLayout.Group>

                {clientPortalStore?.user && (
                  <FormLayout.Group>
                    <Checkbox
                      id="saveCard"
                      size="small"
                      onChange={handleCheckboxChange}
                      label={t("Save card for future bookings")}
                    />
                  </FormLayout.Group>
                )}
              </FormLayout>
            </Form>
          </div>
        </div>
      </Panel>
    </div>
  );
}
